import { useSiteStore } from '~/stores/site.store';
import type { IConfigResponse } from '~/interfaces/dto/config/response';
import { configMapper } from '~/utilities/config-mapper';
import { remapSitemap } from '~/utilities/sitemap-mapper';
import type { IHomePageTiles } from '~/interfaces/dto/content/response';

export const useConfigService = () => {
  const { $fetch } = useNuxtApp();

  const siteStore = useSiteStore();
  const { setLocale } = useLocaleUtils();

  const fetchConfig = async () => {
    return await $fetch<IConfigResponse>(
      `cron/appsettings/jackpotcity/{region}`,
      {
        base: 'config',
        useRegionCode: true,
        keepalive: true,
        useCache: true,
        onResponse({ response }) {
          const remappedConfig = configMapper(response._data);
          siteStore.setConfig(remappedConfig);
        },
        onRequestError(context) {
          console.error('[REQUEST ERROR]:', context);
        },
        onResponseError(context) {
          console.error('[RESPONSE ERROR]:', context);
        },
      },
    );
  };
  const fetchRedirects = async () => {
    return await $fetch<{ fromLink: string; toLink: string }[]>(
      `cron/redirects/jackpotcity/{region}`,
      {
        keepalive: true,
        useRegionCode: true,
        onResponse({ response }) {
          siteStore.setRedirects(response._data);
        },
        onRequestError(context) {
          console.error('[REQUEST ERROR]:', context);
        },
        onResponseError(context) {
          console.error('[RESPONSE ERROR]:', context);
        },
      },
    );
  };
  const fetchFullConfig = async () => {
    const transformAndSetState = (data) => {
      if (!!data?.appsettings) {
        const remappedConfig = configMapper(data?.appsettings);
        siteStore.setConfig(remappedConfig);
      }

      //sitemap
      if (!!data?.sitemaps?.sitemap) {
        const remappedSitemap = remapSitemap(data?.sitemaps?.sitemap);
        let sitemapHost: string = data?.sitemaps?.request?.host;
        if (siteStore.environment === 'production') {
          sitemapHost = sitemapHost.replace(/(.dv|.stg)/, '');
        }
        siteStore.setSitemapHost(sitemapHost);
        siteStore.setSiteMap(remappedSitemap);
      }
      //redirects
      if (!!data?.redirects) {
        siteStore.setRedirects(data?.redirects);
      }
      if (!!data?.locales) {
        setLocale(data?.locales);
      }
    };

    return await $fetch<IConfigResponse>(
      `cron/app-data/{region}/webdesktop/jackpotcity/{languageCode}`,
      {
        base: 'config',
        keepalive: true,
        useRegionCode: true,
        useLanguageCode: true,
        async onResponse({ response }) {
          transformAndSetState(response._data);
        },
      },
    );
  };
  const fetchHomeTiles = async () => {
    return await $fetch<{ secondaryTiles: IHomePageTiles[] }>(
      `cron/homepage/jackpotcity/{region}`,
      {
        base: 'config',
        keepalive: true,
        useRegionCode: true,
        useCache: true,
      },
    );
  };

  return { fetchConfig, fetchRedirects, fetchFullConfig, fetchHomeTiles };
};
